import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone } from "lucide-react";
import {
  pushCapability,
  currentPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isCurrentlySubscribed,
} from "@/lib/pushNotifications";

/**
 * Section for /profile that shows the notifications toggle + iOS-install
 * warning. Placed in a card so it's visually paired with the other profile
 * settings. Refreshes state after every action so the toggle reflects reality
 * (rather than the last click).
 */
export default function PushToggle() {
  const [cap, setCap] = useState("supported");
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setCap(pushCapability());
    setPermission(currentPushPermission());
    try { setSubscribed(await isCurrentlySubscribed()); } catch { setSubscribed(false); }
  };
  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications enabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Failed to enable notifications");
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Notifications disabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Failed to disable");
    } finally { setBusy(false); }
  };

  // iOS Safari (non-standalone) — walk the user through installing first.
  if (cap === "ios-not-installed") {
    return (
      <div className="rounded-lg border border-summit-hairline bg-summit-canvas p-4" data-testid="push-toggle-ios-hint">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-md bg-summit-primary/10 flex items-center justify-center">
            <Smartphone className="w-4 h-4 text-summit-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm font-medium tracking-tight">Notifications on iPhone / iPad</div>
            <p className="mt-1 text-xs text-gray-500 leading-relaxed">
              Apple only allows push notifications for apps installed to the Home Screen.
              <br />
              <b>Tap the Share button</b> in Safari, then <b>Add to Home Screen</b>. Reopen SA
              from the Home Screen, come back to this page, and the toggle will appear.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (cap === "unsupported") {
    return (
      <div className="rounded-lg border border-summit-hairline bg-summit-canvas p-4" data-testid="push-toggle-unsupported">
        <div className="text-sm text-gray-500">Push notifications aren&apos;t supported on this browser.</div>
      </div>
    );
  }

  const isOn = subscribed && permission === "granted";
  return (
    <div className="rounded-lg border border-summit-hairline bg-summit-canvas p-4" data-testid="push-toggle-panel">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center ${isOn ? "bg-emerald-100 text-emerald-700" : "bg-summit-glacier text-summit-primary"}`}>
          {isOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-medium tracking-tight">
            Approval notifications
          </div>
          <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">
            Get a phone alert when someone in your team submits their timesheet for approval.
            Runs on this device only — enable it on every phone you approve from.
          </p>
          <div className="mt-3">
            {isOn ? (
              <button
                type="button"
                onClick={disable}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-summit-hairline px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-white disabled:opacity-50"
                data-testid="push-toggle-disable"
              >
                <BellOff className="w-3.5 h-3.5" /> Disable on this device
              </button>
            ) : (
              <button
                type="button"
                onClick={enable}
                disabled={busy || permission === "denied"}
                className="inline-flex items-center gap-1.5 rounded-md bg-summit-primary text-white px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-summit-primary-soft disabled:opacity-50"
                data-testid="push-toggle-enable"
              >
                <Bell className="w-3.5 h-3.5" /> Enable notifications
              </button>
            )}
            {permission === "denied" && (
              <p className="mt-2 text-[11px] text-rose-600" data-testid="push-toggle-denied">
                Permission was denied. Reset it in your browser settings, then reload this page.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
